import * as React from "react";
import { DatePicker } from "../../../../../react-core/Datepicker/DatePicker";
import { FormComponent } from "../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../react-core/Html/PartLoading";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { addDays, addMonths, addYears } from "../../../../../react-core/utils/date";
import { CompanySearchClient } from "../../../../../Wrap/Api/Company/CompanySearch";
import { NetworkHistoryClient } from "../../../../../Wrap/Api/Network/NetworkHistory";
import { CompanyDetailSort } from "../../../../../Wrap/Constants/Enums/Domain/CompanyDetailSort";
import { FirmType } from "../../../../../Wrap/Constants/Enums/Domain/FirmType";
import { NetworkType } from "../../../../../Wrap/Constants/Enums/Network/NetworkType";
import { TaskRequestStatus } from "../../../../../Wrap/Constants/Enums/Tasks/TaskRequestStatus";
import { CompanySummary } from "../../../../../Wrap/Models/Models/Company/CompanySummary";
import { NetworkHistoryModel } from "../../../../../Wrap/Models/Models/Network/NetworkHistoryModel";
import { RemunerationStatementInfoCollection } from "../../../../../Wrap/Models/Models/Reports/RemunerationStatementInfoCollection";
import { TaskRequestStatusModel } from "../../../../../Wrap/Models/Models/Tasks/TaskRequestStatusModel";
import { Alert } from "../../../../Shared/Components/Alert";
import { AlertType } from "../../../../Shared/Entities/Constants/AlertConstants";
import { ExcelExportAction } from "../../../../Shared/ExcelExport/Actions/ExcelExportAction";
import { SearchInput } from "../../../../Shared/SearchInput/SearchInput";
import { DocumentFileAction } from "../../Clients/PortfolioDashboard/Components/Document/Action/DocumentFileAction";
import { datePickerOptions } from "../../Clients/Common/Resource/Contents";
import { ReportsAction } from "../Actions/ReportsAction";
import { ReportTypeArray, ReportTypeMap, ReportType } from "../Resource/Contents";
import { RemunerationStatementSearchResultTable } from "./RemunerationStatementSearchResultTable";
import { ErrorMessagePopup } from "../../../../Shared/Components/ErrorMessagePopup";

interface IRemunerationStatementState {
    tableData: RemunerationStatementInfoCollection;
    isLoading: boolean;
    reportType: any;
    network: NetworkHistoryModel;
    firm: CompanySummary;
    startDate: Date;
    endDate: Date;
    displayNetworkSearch: boolean;
    displayCompanySearch: boolean;
    displayNoNetworFirmMsg: boolean;
    haveOnlyOneNetwork: boolean;
    haveOnlyOneCompany: boolean;
    isShowErrorMessage: boolean;
}

export class RemunerationStatement extends FormComponent<null, IRemunerationStatementState> {
    constructor(props: any) {
        super(props);
        this.state = {
            tableData: null,
            isLoading: false,
            reportType: ReportType.RemunerationStatement,
            network: null,
            firm: null,
            startDate: addDays(addMonths(new Date(), -3), 1),
            endDate: new Date(),
            displayNetworkSearch: false,
            displayCompanySearch: false,
            displayNoNetworFirmMsg: false,
            haveOnlyOneNetwork: false,
            haveOnlyOneCompany: false,
            isShowErrorMessage: false,
        };
    }

    componentDidMount() {
        const networkClient = new NetworkHistoryClient();
        const companyClient = new CompanySearchClient();
        networkClient.get(null, NetworkType.Unknown, null, null, null, true, 10, 1).onSuccess((result) => {
            if (result.TotalRows > 1) {
                this.setState({ displayNetworkSearch: true });
            } else if (result.TotalRows == 1) {
                this.setState({ network: result?.networks[0], haveOnlyOneNetwork: true });
            }
            companyClient
                .get(null, null, FirmType.All, null, null, null, true, 1, 10, CompanyDetailSort.CompanyName, false)
                .onSuccess((result) => {
                    if (result.TotalNumberOfResults > 1) {
                        this.setState({ displayCompanySearch: true });
                    } else if (result.TotalNumberOfResults == 1) {
                        this.setState({ firm: result?.PageOfResults[0], haveOnlyOneCompany: true });
                    }
                })
                .onSuccess(() => {
                    if (
                        this.state.haveOnlyOneNetwork ||
                        (this.state.haveOnlyOneCompany && this.state.network == null)
                    ) {
                        this.onClickSearch(1);
                    }
                });
        });
    }

    private renderFilter() {
        const { reportType, network, firm, startDate, endDate } = this.state;
        const networkClient = new NetworkHistoryClient();
        const companyClient = new CompanySearchClient();
        return (
            <>
                {this.state.displayNoNetworFirmMsg == true && (
                    <Alert alertType={AlertType.Error} message={"Must select a network or a company"} />
                )}
                <div className="row">
                    <div className="col-12 col-sm-4">
                        <SelectEditor
                            options={ReportTypeArray}
                            getText={(v) => {
                                return ReportTypeMap[v];
                            }}
                            placeholder="Please select"
                            label="Report type"
                            selected={reportType}
                            required={"Please select report type"}
                            onChange={(v) => {
                                this.setState({ reportType: v });
                            }}
                        />
                    </div>
                    {this.state.displayNetworkSearch && (
                        <div className="col-12 col-sm-4">
                            <SearchInput
                                apiClient={(v) => {
                                    return networkClient.get(null, NetworkType.Unknown, v, null, null, true, 20, 1);
                                }}
                                onClick={(v: NetworkHistoryModel) => {
                                    this.setState({ network: v, firm: null });
                                }}
                                resultKey="networks"
                                getText={(v: NetworkHistoryModel) => v.NetworkName}
                                label={"Network"}
                                value={network?.NetworkName}
                            />
                        </div>
                    )}
                    {this.state.displayCompanySearch && (
                        <div className="col-12 col-sm-4">
                            <SearchInput
                                apiClient={(v) => {
                                    return companyClient.get(
                                        null,
                                        v,
                                        FirmType.All,
                                        null,
                                        network?.Network,
                                        null,
                                        true,
                                        1,
                                        40,
                                        CompanyDetailSort.CompanyName,
                                        false
                                    );
                                }}
                                resultKey={"PageOfResults"}
                                onClick={(v: CompanySummary) => {
                                    this.setState({ network: null, firm: v });
                                }}
                                getText={(v: CompanySummary) => v.CompanyName}
                                label={"Company"}
                                value={firm?.CompanyName}
                            />
                        </div>
                    )}
                </div>
                <div className="row">
                    <div className="col-sm-4 col-12">
                        <DatePicker
                            label="Requested between"
                            selected={startDate}
                            onChange={(v) => {
                                this.setState({ startDate: v });
                            }}
                            atLeast={
                                this.state.endDate
                                    ? {
                                          value: addYears(endDate, -1),
                                          errorMessage: "Maximum date range is one year",
                                      }
                                    : null
                            }
                            atMost={{
                                value: endDate
                                    ? endDate
                                    : new Date(),
                                errorMessage: "Start date of period selected must be before end date",
                            }}
                            alwaysValidateOnUpdate={false}
                            {...datePickerOptions}
                            required="Please select start date"
                        />
                    </div>
                    <div className="col-sm-4 col-12">
                        <DatePicker
                            label="And"
                            selected={endDate}
                            onChange={(v) => {
                                this.setState({ endDate: v });
                            }}
                            atLeast={
                                startDate
                                    ? {
                                          value: startDate,
                                          errorMessage: "End date of period selected must be after start date",
                                      }
                                    : null
                            }
                            atMost={{ 
                                value: startDate
                                    ? addYears(startDate, 1).getTime() > new Date().getTime()
                                        ? new Date()
                                        : addYears(startDate, 1)
                                    : new Date(),
                                errorMessage: "Maximum date range is one year",
                            }}
                            alwaysValidateOnUpdate={false}
                            {...datePickerOptions}
                            required="Please select end date"
                        />
                    </div>
                    <div className="col-sm-4 col-12">
                        <button className="btn primary mt-30" onClick={() => this.onClickSearch(1)}>
                            Go
                        </button>
                    </div>
                </div>
            </>
        );
    }
    public checkStatus(taskRequestID: number, status: TaskRequestStatus) {
        const { tableData } = this.state;
        if (
            status == TaskRequestStatus.Pending ||
            status == TaskRequestStatus.Running ||
            status == TaskRequestStatus.Allocated ||
            status == TaskRequestStatus.PendingDelayed ||
            status == TaskRequestStatus.PendingHold
        ) {
            setTimeout(() => {
                ExcelExportAction.getTaskRequestStatus(taskRequestID.toString(), (res: TaskRequestStatusModel) => {
                    this.checkStatus(taskRequestID, res?.Status);
                });
            }, 2000);
        } else if (status == TaskRequestStatus.Complete) {
            //TODO replace the dummy file name, currently is fine as we use generic file name
            DocumentFileAction.get("", "doc.xls", true, 0, taskRequestID, null, (result: string) => {
                tableData.PageOfResults.find((x) => x.TaskRequestID == taskRequestID).TaskRequestStatus =
                    TaskRequestStatus.Complete;
                tableData.PageOfResults.find((x) => x.TaskRequestID == taskRequestID).File = result;
                this.setState({ tableData: tableData });
            }, true);
        } else {
            tableData.PageOfResults.find((x) => x.TaskRequestID == taskRequestID).TaskRequestStatus = status;
            this.setState({ tableData: tableData });
        }
    }
    public isValid(): boolean {
        let flag = super.isValid();
        if (this.state.network == null && this.state.firm == null) {
            flag = false;
            this.setState({ displayNoNetworFirmMsg: true });
        } else {
            this.setState({ displayNoNetworFirmMsg: false });
        }
        return flag;
    }
    private onClickSearch(pageIndex: number) {
        if (this.isValid()) {
            const { network, firm, startDate, endDate } = this.state;
            ReportsAction.getRemunerationStatementHistory(
                startDate,
                endDate,
                network?.Network,
                firm?.CompanyCode,
                (pageIndex - 1) * 10 + 1,
                10,
                (data: RemunerationStatementInfoCollection) => {
                    data?.PageOfResults?.forEach((x) => {
                        if (
                            x.TaskRequestStatus == TaskRequestStatus.Pending ||
                            x.TaskRequestStatus == TaskRequestStatus.Running ||
                            x.TaskRequestStatus == TaskRequestStatus.Allocated ||
                            x.TaskRequestStatus == TaskRequestStatus.PendingDelayed ||
                            x.TaskRequestStatus == TaskRequestStatus.PendingHold
                        ) {
                            ExcelExportAction.getTaskRequestStatus(
                                x.TaskRequestID.toString(),
                                (res: TaskRequestStatusModel) => {
                                    this.checkStatus(x.TaskRequestID, res.Status);
                                }
                            );
                        }
                    });

                    this.setState({ tableData: data });
                }
            );
        }
    }

    closeErrorMessagePopup = () => {
        this.setState({ isShowErrorMessage: false });
    };

    private renderTable(data: any) {
        return <RemunerationStatementSearchResultTable data={data} onPageChange={(s, isShowErr) => this.onPageChange(s, isShowErr)} />;
    }

    private renderErrorMessage(): JSX.Element {
        const { isShowErrorMessage } = this.state;
        if (isShowErrorMessage) {
            return (
                <div>
                    <p>
                        <ErrorMessagePopup
                            showPopup={isShowErrorMessage}
                            errorMessage={"Failed to load document."}
                            onClose={this.closeErrorMessagePopup}
                        />
                    </p>
                </div>
            );
        }
    }

    private onPageChange(pageIndex: number, isShowError: boolean) {
        if (pageIndex != -1) {
            this.onClickSearch(pageIndex);
        } else {
            if (isShowError){
                this.setState({isShowErrorMessage: true});
            }
        }
    }

    public render(): JSX.Element {
        const { tableData, isLoading } = this.state;
        return (
            <div>
                <h1 className="page-heading">Remuneration Statement History</h1>
                {this.renderFilter()}
                {isLoading ? <PartLoading isLoading={true}></PartLoading> : tableData && this.renderTable(tableData)}
                {this.renderErrorMessage()}
            </div>
        );
    }
}
